import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';

import { GapiLoaderService } from 'src/app/app-common/services/gapi-loader.service';
import { UserauthApiService } from 'src/app/api/services/userauth-api.service';
import { RvTokenStoreService } from './rv-token-store.service';
import { UserStateService } from './user-state.service';
import { VisibilityService } from './visibility.service';

@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService {
  static readonly POLL_INTERVAL_MS = 60 * 60 * 1000;
  static readonly REFRESH_THRESHOLD_MS = 60 * 60 * 1000;

  private timerId: ReturnType<typeof setInterval> | null = null;
  private visibilitySubscription: Subscription | null = null;

  constructor(
    private userauthApiService: UserauthApiService,
    private userStateService: UserStateService,
    private rvTokenStoreService: RvTokenStoreService,
    private visibilityService: VisibilityService,
    private gapiLoaderService: GapiLoaderService,
  ) {}

  start(): void {
    this.stop();

    if (!this.userStateService.isRiseAuthUser()) {
      return;
    }

    this.timerId = setInterval(
      () => this._refreshIfExpiring(),
      TokenRefreshService.POLL_INTERVAL_MS,
    );

    this.visibilitySubscription =
      this.visibilityService.visibilityChange$.subscribe((visible) => {
        if (visible) {
          this._refreshIfExpiring();
        }
      });

    this._refreshIfExpiring();
  }

  stop(): void {
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }

    if (this.visibilitySubscription) {
      this.visibilitySubscription.unsubscribe();
      this.visibilitySubscription = null;
    }
  }

  private _refreshIfExpiring(): void {
    const userToken = this.userStateService._state.userToken;
    const rawJwt = userToken?.token?.access_token;

    if (!rawJwt) {
      this.stop();
      return;
    }

    const expMs = this._readJwtExpMs(rawJwt);

    if (expMs === null) {
      return;
    }

    if (expMs - Date.now() > TokenRefreshService.REFRESH_THRESHOLD_MS) {
      return;
    }

    this._refresh(userToken, rawJwt);
  }

  private _refresh(userToken: any, rawJwt: string): void {
    this.userauthApiService
      .refreshToken(rawJwt)
      .then((result) => {
        const newJwt = result?.item;

        if (!newJwt) {
          return;
        }

        const newAccessToken = {
          access_token: newJwt,
          expires_in: '3600',
          token_type: 'Bearer',
        };
        const newUserToken = {
          ...userToken,
          token: newAccessToken,
        };

        this.userStateService._state.userToken = newUserToken;
        this.userStateService.setAccessToken(newAccessToken);
        this.rvTokenStoreService.write(newUserToken);

        this.gapiLoaderService
          .load()
          .then((gApi) => gApi.client.setToken(newAccessToken));
      })
      .catch((err) => {
        console.debug('Token refresh failed', err);
      });
  }

  private _readJwtExpMs(rawJwt: string): number | null {
    try {
      const parts = rawJwt.split('.');

      if (parts.length < 2) {
        return null;
      }

      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const claims = JSON.parse(atob(padded));

      return typeof claims.exp === 'number' ? claims.exp * 1000 : null;
    } catch {
      return null;
    }
  }
}
